import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiClient from '@/api/client';
import { announceTreeRestore } from './treeRestoreResult';
import { TreeRestorePicker, type Inspection, type RestoreChoice } from './TreeRestorePicker';

// ─── Einzelnen Stammbaum aus einem Backup auf der Storage Box ────────────────
//
// Dieselbe Auswahl wie bei einer hochgeladenen Datei; nur wird das Backup nicht
// hochgeladen, sondern vom Server geholt. Es wandert also nicht durch den
// Browser – bei mehreren hundert Megabyte ist das der springende Punkt.

export function TreeFromRemoteBackup({
  filename,
  onDone,
}: {
  filename: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [info, setInfo] = useState<Inspection | null>(null);
  const [busy, setBusy] = useState<null | 'inspect' | 'restore'>('inspect');
  const [failed, setFailed] = useState(false);

  // Beim Öffnen einmal hineinsehen. Der Server lädt die Datei dafür von der
  // Storage Box – das kann bei großen Backups dauern.
  useEffect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const { data } = await apiClient.post<Inspection>(
          `/backup/hetzner/trees/${encodeURIComponent(filename)}`,
        );
        if (!abgebrochen) setInfo(data);
      } catch (err) {
        if (abgebrochen) return;
        setFailed(true);
        toast.error(err instanceof Error ? err.message : 'Backup konnte nicht gelesen werden.');
      } finally {
        if (!abgebrochen) setBusy(null);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [filename]);

  const handleRestore = async (choice: RestoreChoice) => {
    setBusy('restore');
    try {
      const { data } = await apiClient.post(
        `/backup/hetzner/restore-tree/${encodeURIComponent(filename)}`,
        choice,
      );
      announceTreeRestore(data);
      await queryClient.invalidateQueries();
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Wiederherstellung fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <p className="text-xs font-medium text-slate-700">
        Einzelnen Stammbaum aus <span className="font-mono">{filename}</span> holen
      </p>

      {busy === 'inspect' && (
        <p className="mt-2 text-xs text-slate-500">
          Backup wird von der Storage Box geladen und gelesen…
        </p>
      )}

      {failed && (
        <button
          onClick={onDone}
          className="mt-2 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Schließen
        </button>
      )}

      {info && (
        <TreeRestorePicker
          info={info}
          busy={busy === 'restore'}
          onRestore={(c) => void handleRestore(c)}
          onCancel={onDone}
        />
      )}
    </div>
  );
}
